import { ModeOfPlay } from '../models/IBattlePack.js';
import { toAoSData } from '../models/IAbility.js';
// NOTE: For authored IBattleplans, rulesText must contain only the "rules section"
// narrative — NOT the twist or scoringText, which are emitted as separate components
// below. Parser-built IBattleplans store ALL component text in rulesText, so do not
// call this function on them (it would double-index the twist/scoring content).
// Build the component list for one battleplan, matching the shape the rules
// browser renders and the search index reads (see RuleSection.battleplanFromContainer
// and makeSearchableRuleAbilities). Order mirrors the official documents:
// twist, then abilities, then scoring. Ability components carry the raw `ability`
// (IRuleAbility) field — the renderer rebuilds each ability via fromAoSData, so
// emitting only `text` would render no ability cards.
function componentsFor(bp) {
    const components = [];
    if (bp.sections?.length) {
        for (const section of bp.sections) {
            const title = section.title.trim();
            if (title)
                components.push({ contentType: 'header', title, text: title });
            if (section.text)
                components.push({ contentType: 'text', text: section.text });
            for (const ability of section.abilities ?? []) {
                components.push({
                    contentType: 'ability',
                    title: ability.name,
                    text: ability.effect,
                    ability: toAoSData(ability),
                });
            }
            if (section.trailingText) {
                components.push({ contentType: 'text', text: section.trailingText });
            }
        }
        return components;
    }
    if (bp.twist)
        components.push({ contentType: 'twist', text: bp.twist });
    for (const ability of bp.abilities) {
        components.push({
            contentType: 'ability',
            title: ability.name,
            // Effect text kept for search; the renderer uses `ability` for the card.
            text: ability.effect,
            ability: toAoSData(ability),
        });
    }
    if (bp.scoringText)
        components.push({ contentType: 'scoring', text: bp.scoringText });
    // Rules narrative — rendered above the twist on the card and indexed for search.
    if (bp.rulesText)
        components.push({ contentType: 'text', text: bp.rulesText });
    return components;
}
// Serialize an authored battleplan document into a RuleDocument so authored
// White Dwarf / Path to Glory battleplans are findable in the rules browser.
export function battleplanDocumentToRuleDocument(doc) {
    // A bundle's battlePack may be a homebrew CustomBattlePack, which RuleDocument's
    // (app-wide IBattlePack) pack type doesn't model. Known/PtG packs pass through
    // unchanged; a custom pack is carried as-is for search. Narrow here at the seam.
    const battlePack = doc.battlePack;
    return {
        title: doc.title,
        modeOfPlay: ModeOfPlay.MatchedPlay,
        battlePack,
        sections: doc.sections.map((section) => ({
            name: section.name,
            subSections: [],
            modeOfPlay: ModeOfPlay.MatchedPlay,
            battlePack,
            containers: section.containers.map((container) => ({
                name: container.name,
                containerType: container.containerType,
                components: componentsFor(container.battleplan),
            })),
        })),
    };
}
